import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'

import { SearchMenuStates } from '../type'
import DateInputDesktop from './SearchDateInput/SearchDateInputDesktop'
import TravellersInputDesktop from './SearchTravellersInput/SearchTravellersInputDesktop'

import { isRoomsInvalid } from 'lib/searchUtils'

import TextButton from 'components/Luxkit/Button/TextButton'

import zIndex from 'styles/tools/z-index'
import Backdrop from 'components/utils/Backdrop'
import { DATE_SEARCH_OPTION_IDS, LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL, LOCATION_SEARCH_INPUT_TITLE_LABEL } from 'constants/search'
import config from 'constants/config'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchLocationInput from './SearchLocationInput/SearchLocationInput'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineArrowLeftIcon from 'components/Luxkit/Icons/line/LineArrowLeftIcon'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import LineFilterAltIcon from 'components/Luxkit/Icons/line/LineFilterAltIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const InputWrapper = styled.section`
  ${mediaQueryUp.sm} {
    width: 100%;
    position: relative;
    gap: ${rem(8)};
    display: grid;
    grid-template-areas:
      "location-input location-input location-input"
      "date-input traveller-input actions";
    grid-template-columns: minmax(${rem(348)}, 1fr) minmax(${rem(180)}, 1fr) ${rem(112)};
  

    &.has-dropdown {
      z-index: ${zIndex.menu};
    }
  }

  ${mediaQueryUp.md} {
    grid-template-areas: "location-input date-input traveller-input actions";
    grid-template-columns: 1fr ${rem(320)} ${rem(154)} ${rem(112)};
  }

  &.map-view {
    grid-template-areas: "arrow location-input date-input traveller-input actions filters";
    grid-template-columns: 1fr minmax(${rem(154)}, 1fr) ${rem(320)} ${rem(154)} ${rem(48)} 1fr;
    ${mediaQueryUp.lg} {
      grid-template-columns: 1fr min(${rem(348)}, 1fr) ${rem(320)} ${rem(154)} ${rem(48)} 1fr;
    }
  }
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  &.location-input {
    grid-area: location-input;
  }

  &.date-input {
    grid-area: date-input;
  }

  &.traveller-input {
    grid-area: traveller-input;
  }

  &.actions {
    grid-area: actions;
  }

  &.arrow {
    grid-area: arrow;
    display: flex;
    align-items: flex-start;
  }

  &.filters {
    grid-area: filters;
    display: flex;
    align-items: flex-end;
  }
`

const StyledBackdrop = styled(Backdrop)`
  z-index: 0;
  &.visible {
    background-color: rgba(0, 0, 0, 0) !important; /* to prevent original background grey color when the backdrop open too fast too often */
    transition: none;
  }
`

const SearchButton = styled(TextButton)`
  height: ${rem(50)};
`

interface Props {
  searchItem: App.SearchItem;
  rooms: App.Occupants[];
  openDatePickerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openTravellerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openLocationMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  closeMenu: () => void;
  activeMenu: SearchMenuStates;
  onRoomChange: (rooms: Array<App.Occupants>) => void;
  onRoomsApply: () => void;
  onDatesApply: () => void;
  onDatesDecline: () => void;
  showRoomErrors: boolean;
  dropdownToggled: boolean;
  backdropOnClick?: () => void;
  backdropVisibility?: boolean;
  anytimeDateSelected?: boolean;
  selectAnytimeDate?: (shouldApply: boolean) => void;
  selectSpecificDates?: () => void;
  selectFlexibleDates?: () => void;
  onFlexibleMonthSelected?: () => void;
  onFlexibleNightsSelected?: () => void;
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  onLocationChange?: (item: App.SearchItem) => void;
  saleUnit?: string;
  typeaheadTypes?: Array<App.SearchPlaceType>;
  recentSearches?: Array<App.FullRecentSearch>;
  switchView?: {
    label: string,
    view: 'map' | 'list'
    URL: string
  }
  onToggleFilters?: () => void;
}

const SearchDesktopInputs = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    activeMenu,
    searchItem,
    rooms,
    openTravellerMenu,
    openLocationMenu,
    closeMenu,
    openDatePickerMenu,
    onRoomChange,
    onRoomsApply,
    onDatesApply,
    onDatesDecline,
    showRoomErrors,
    dropdownToggled,
    backdropOnClick,
    backdropVisibility,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    onLocationChange,
    saleUnit,
    typeaheadTypes,
    recentSearches,
    switchView,
    onToggleFilters,
  } = props
  const dispatch = useAppDispatch()
  const { checkinDate, checkoutDate } = useContext(GlobalSearchStateContext)

  const isFormInvalid = useMemo(() => isRoomsInvalid(rooms), [rooms])

  const datesSelected = !!(checkinDate && checkoutDate)
  const isSearchDisabled = config.businessTraveller.currentAccountMode === 'business' && !datesSelected

  const toListView = useCallback((e) => {
    e.preventDefault()
    if (!switchView) return
    dispatch(pushWithRegion(switchView.URL))
  }, [dispatch, switchView])

  return <>
    <InputWrapper className={cn({ 'has-dropdown': dropdownToggled, 'map-view': switchView?.view === 'list' })}>
      {switchView?.view === 'list' &&
        <InputContainer className="arrow">
          <CSSBreakpoint min="lg">
            <TextButton
            kind="tertiary"
            size="large"
            onClick={toListView}
            className="list-view"
            aria-label="list-view"
            data-testid="list-view"
            startIcon={<LineArrowLeftIcon/>}
        >
              {switchView.label}
            </TextButton>
          </CSSBreakpoint>
          <CSSBreakpoint max="md">
            <IconButton kind="tertiary" onClick={toListView} size="large">
              <LineArrowLeftIcon />
            </IconButton>
          </CSSBreakpoint>
        </InputContainer>
      }
      <InputContainer className="location-input">
        <SearchLocationInput
          label={LOCATION_SEARCH_INPUT_TITLE_LABEL}
          placeholder={LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL}
          placeTypes={typeaheadTypes}
          onChange={onLocationChange}
          variant="dropdown"
          inputRef={ref}
          recentSearchItems={recentSearches}
          onLocationToggle={openLocationMenu}
        />
      </InputContainer>
      <InputContainer className="date-input">
        <DateInputDesktop
          onDateToggle={openDatePickerMenu}
          isToggled={activeMenu === SearchMenuStates.Dates}
          onDatesApply={onDatesApply}
          onDatesDecline={onDatesDecline}
          isLastStep={searchItem && rooms.length > 0}
          anytimeDateSelected={anytimeDateSelected}
          selectAnytimeDate={selectAnytimeDate}
          selectSpecificDates={selectSpecificDates}
          selectFlexibleDates={selectFlexibleDates}
          dateSearchOptionId={dateSearchOptionId}
          isMapView={switchView?.view === 'list'}
        />
      </InputContainer>
      <InputContainer className="traveller-input">
        <TravellersInputDesktop
          rooms={rooms}
          onTravellersToggle={openTravellerMenu}
          isToggled={activeMenu === SearchMenuStates.Rooms}
          onRoomChange={onRoomChange}
          onRoomsApply={onRoomsApply}
          closeMenu={closeMenu}
          isLastStep={!!searchItem}
          error={showRoomErrors && isFormInvalid}
          saleUnit={saleUnit}
          isMapView={switchView?.view === 'list'}
        />
      </InputContainer>
      {switchView?.view !== 'list' &&
        <InputContainer className="actions">
          <SearchButton disabled={isSearchDisabled} kind="primary" type="submit" size="large">Search</SearchButton>
        </InputContainer>
      }
      {switchView?.view === 'list' &&
        <InputContainer className="actions">
          <IconButton disabled={isSearchDisabled} kind="primary" type="submit" size="large">
            <LineSearchIcon />
          </IconButton>
        </InputContainer>
      }
      {switchView?.view === 'list' &&
        <InputContainer className="filters">
          <CSSBreakpoint min="lg">
            <TextButton kind="secondary" onClick={onToggleFilters} startIcon={<LineFilterAltIcon />} size="large">
              Filters
            </TextButton>
          </CSSBreakpoint>
        </InputContainer>
      }
    </InputWrapper>
    <StyledBackdrop
      visible={backdropVisibility || activeMenu !== SearchMenuStates.Closed}
      onClick={backdropOnClick || closeMenu}
    />
  </>
})

export default SearchDesktopInputs
